/** @format */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom"; // Use Link
import { PayPalButton } from "react-paypal-button-v2"; // Keep V2
import { motion, AnimatePresence } from "framer-motion";
import { usePlacesWidget } from "react-google-autocomplete";
import {
	Elements,
	PaymentElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
	MapPin,
	CreditCard,
	Lock,
	ShoppingCart,
	User,
	Mail,
	Phone,
	Edit,
	ChevronLeft,
	ArrowRight,
	Info,
} from "lucide-react"; // Added/refined icons

// Actions, Components, Utils (Ensure paths are correct)
import {
	cancelPromoCode,
	saveCartSummary,
	savePaymentResult,
	saveShippingAddress,
} from "../actions/cartActions";
import {
	createOrder,
	createPaymentIntent,
	getRates,
} from "../actions/orderActions";
import {
	addEmailToCartMailChimp,
	getUserDetails,
	login,
} from "../actions/userActions";
import AffirmCheckout from "../components/AffirmCheckout"; // Assuming path
import { Toast } from "./customize/Toast"; // Assuming path
import { FormInput } from "../components/FormInput"; // Assuming path
import { CartSummary } from "../components/CartSummary"; // Assuming path
import { CartItem } from "../components/CartItem"; // Import CartItem for display
import { calculateTotals } from "../utils/calulateTotals"; // Assuming path
import { LoadingSpinner } from "../components/LoadingSpinner"; // Assuming path

// Load Stripe (outside component)
const stripePromise = loadStripe(process.env.REACT_APP_PUBLIC_STRIPE);

// --- Stripe Component (Keep as is, maybe slight style tweaks) ---
const StripePaymentComponent = ({
	successPaymentHandler,
	payer,
	orderTotal,
}) => {
	const [isProcessing, setIsProcessing] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const stripe = useStripe();
	const elements = useElements();

	const handleSubmit = async (e) => {
		// Guard clauses
		if (!stripe || !elements) {
			console.error("Stripe.js has not loaded.");
			return;
		}
		if (isProcessing) return; // Prevent double-clicks

		setIsProcessing(true);
		setErrorMessage("");

		const { error, paymentIntent } = await stripe.confirmPayment({
			elements,
			redirect: "if_required", // Don't redirect automatically, handle result here
		});

		if (error) {
			console.error("Stripe confirmPayment error:", error);
			setErrorMessage(
				error.message || "Payment failed. Please check your card details."
			);
		} else if (paymentIntent && paymentIntent.status === "succeeded") {
			console.log("Stripe Payment Succeeded:", paymentIntent);
			successPaymentHandler({
				// Call parent handler with standardized structure
				status: "COMPLETED",
				id: paymentIntent.id,
				method: "Stripe", // Add payment method
				payer: payer, // Pass payer info
				purchase_units: [
					{
						amount: {
							value: paymentIntent.amount / 100,
							currency_code: paymentIntent.currency,
						},
					},
				], // Mimic PayPal structure
			});
			// Don't navigate here, let parent handle it after order update
		} else {
			console.warn("Stripe Payment Intent status:", paymentIntent?.status);
			setErrorMessage("Payment processing failed or requires further action.");
		}
		setIsProcessing(false);
	};

	const paymentElementOptions = { layout: "tabs" }; // Or 'accordion'

	return (
		<div className="space-y-4">
			<PaymentElement options={paymentElementOptions} />
			{errorMessage && (
				<div className="text-red-600 text-sm mt-2">{errorMessage}</div>
			)}
			<button
				type="button"
				onClick={handleSubmit}
				disabled={isProcessing || !stripe || !elements}
				className="w-full btn btn-primary mt-4 py-3 text-base" // Use consistent button style
			>
				{isProcessing ? (
					<LoadingSpinner size="sm" text="Processing..." />
				) : (
					`Pay $${orderTotal.toFixed(2)}`
				)}
			</button>
		</div>
	);
};

// --- Payment Method Card (Revised Styling) ---
const PaymentMethodCard = ({
	selected,
	onClick,
	icon,
	name,
	children,
	value,
	disabled = false,
}) => (
	<label
		className={`cursor-pointer relative ${
			disabled ? "opacity-50 cursor-not-allowed" : ""
		}`}
	>
		<input
			type="radio"
			name="paymentMethod" // Group radios
			className="sr-only peer" // Hide default radio visually
			value={value}
			checked={selected}
			onChange={onClick}
			disabled={disabled}
		/>
		{/* Custom styled card */}
		<div
			className={`p-4 rounded-lg border-2 text-center transition-all duration-150 ${
				selected
					? "border-primary bg-primary/5 ring-2 ring-primary/20"
					: "border-gray-200 bg-white hover:border-gray-300"
			} peer-focus:ring-2 peer-focus:ring-primary/50 peer-focus:border-primary`}
		>
			<div className="flex flex-col items-center justify-center gap-1.5 h-full">
				<div className="h-8 flex items-center justify-center">{icon}</div>
				<span className="text-xs font-medium mt-1">{name}</span>
			</div>
		</div>
		{/* Render children (like Affirm logo specifics) if needed */}
		{children}
	</label>
);

// --- Helper Functions (Keep or move to utils) ---
const buildParcels = (cartItems) => {
	/* ... same logic ... */
	return cartItems.map((item) => {
		const weight = {
			value: (parseFloat(item.optionInfo.widthWhole) || 0) * 0.15,
			unit: "kg",
		};
		const dimension = {
			width: 7,
			height: 7,
			depth: Number(item.optionInfo.widthWhole) || 10,
			unit: "in",
		};
		let quantity = item.optionInfo.quantity || 1;
		return {
			description: "Custom Blind",
			box_type: "custom",
			weight,
			dimension,
			items: [
				{
					description: "Blind",
					origin_country: "CA",
					quantity: quantity,
					price: {
						amount:
							(parseFloat(item.optionInfo.OUR_PRICE) / quantity).toFixed(2) ||
							10,
						currency: "CAD",
					},
					weight: { value: weight.value / quantity, unit: "kg" },
					sku: item.productInfo?.Fabric_Code || "CUSTOM",
				},
			],
		};
	});
};
const buildShippingAddress = (shippingAddress, countryState) => {
	/* ... same logic ... */
	return {
		contact_name: `${shippingAddress.firstName || ""} ${
			shippingAddress.lastName || ""
		}`.trim(),
		street1: shippingAddress.addressLine1 || "",
		street2: shippingAddress.addressLine2 || "",
		city: shippingAddress.city || "",
		state: shippingAddress.province || "",
		postal_code: (shippingAddress.postalCode || "").replace(/\s/g, ""),
		country: countryState === "USA" ? "US" : "CA",
		phone: shippingAddress.phoneNumber || "",
		email: shippingAddress.email || "",
		type: "residential",
	};
};

// --- Main Checkout Screen Component ---
const PlaceOrderScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// --- Redux State ---
	const { cartItems = [], cartSummary = {} } = useSelector(
		(state) => state.cart
	);
	const { userInfo, isGuest } = useSelector((state) => state.userLogin);
	const userId = userInfo?.id || null;
	const countryState = useSelector((state) => state.country);
	const currencyCode = countryState === "USA" ? "USD" : "CAD";
	const { order: createdOrder, error: orderError } = useSelector(
		(state) => state.orderCreate
	); // Get created order ID/error

	// --- Component State ---
	const [currentStep, setCurrentStep] = useState(1); // 1: Shipping, 2: Payment
	const [loadingStep1, setLoadingStep1] = useState(false); // Loading for address confirm / rate fetch / intent creation
	const [loadingPayment, setLoadingPayment] = useState(false); // Loading for final payment processing/order update
	const [clientSecret, setClientSecret] = useState(""); // For Stripe
	const [paymentError, setPaymentError] = useState(""); // Errors during final payment
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe"); // 'stripe', 'paypal', 'affirm'
	const [formErrors, setFormErrors] = useState({});
	const [toast, setToast] = useState(null);

	// Calculated Totals State (after shipping)
	const [finalTotals, setFinalTotals] = useState(() =>
		calculateTotals(cartItems, countryState, 0, cartSummary?.withPromoPrice, "")
	); // Initial calculation without shipping/tax
	const [shippingRate, setShippingRate] = useState(0); // Store fetched shipping rate

	// Form state (initialize with Redux or UserInfo)
	const [formData, setFormData] = useState({
		firstName:
			userInfo?.name?.split(" ")[0] ||
			cartSummary?.shippingAddress?.firstName ||
			"",
		lastName:
			userInfo?.name?.split(" ").slice(1).join(" ") ||
			cartSummary?.shippingAddress?.lastName ||
			"",
		email: userInfo?.email || cartSummary?.shippingAddress?.email || "",
		phoneNumber: cartSummary?.shippingAddress?.phoneNumber || "",
		addressLine1: cartSummary?.shippingAddress?.addressLine1 || "",
		addressLine2: cartSummary?.shippingAddress?.addressLine2 || "",
		city: cartSummary?.shippingAddress?.city || "",
		province: cartSummary?.shippingAddress?.province || "",
		postalCode: cartSummary?.shippingAddress?.postalCode || "",
		country: countryState || "Canada",
		howDidYouHear: cartSummary?.shippingAddress?.howDidYouHear || "",
	});

	// Update form if country changes globally
	useEffect(() => {
		setFormData((prev) => ({ ...prev, country: countryState }));
		setFinalTotals(
			calculateTotals(
				cartItems,
				countryState,
				shippingRate,
				cartSummary?.withPromoPrice,
				formData.province
			)
		);
	}, [countryState]);

	// Pre-fill from UserInfo if not already filled
	useEffect(() => {
		if (
			userInfo &&
			!isGuest &&
			!formData.firstName &&
			!cartSummary?.shippingAddress?.firstName
		) {
			setFormData((prev) => ({
				...prev,
				firstName: userInfo.name?.split(" ")[0] || "",
				lastName: userInfo.name?.split(" ").slice(1).join(" ") || "",
				email: userInfo.email || "",
			}));
		}
	}, [
		userInfo,
		isGuest,
		formData.firstName,
		cartSummary?.shippingAddress?.firstName,
	]);

	// --- Google Places ---
	const { ref: placesRef } = usePlacesWidget({
		/* ... same config ... */ apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
		onPlaceSelected: (place) => {
			if (!place?.address_components) return;
			const getComponent = (type) =>
				place.address_components.find((c) => c.types.includes(type));
			const streetNumber = getComponent("street_number")?.long_name || "";
			const street = getComponent("route")?.long_name || "";
			const city = getComponent("locality")?.long_name || "";
			const province =
				getComponent("administrative_area_level_1")?.short_name || "";
			const postalCode = getComponent("postal_code")?.long_name || "";
			const countryCode = getComponent("country")?.short_name?.toUpperCase();
			setFormData((prev) => ({
				...prev,
				addressLine1: `${streetNumber} ${street}`.trim(),
				city,
				province,
				postalCode,
				country: countryCode === "US" ? "USA" : "Canada",
			}));
			setFormErrors((prev) => ({
				...prev,
				addressLine1: undefined,
				city: undefined,
				province: undefined,
				postalCode: undefined,
			}));
		},
		options: {
			types: ["address"],
			componentRestrictions: { country: countryState === "USA" ? "us" : "ca" },
			fields: ["address_components"],
		},
	});

	// --- Validation ---
	const validateForm = useCallback(() => {
		/* ... same validation logic ... */
		const newErrors = {};
		const requiredFields = [
			"firstName",
			"lastName",
			"email",
			"phoneNumber",
			"addressLine1",
			"city",
			"province",
			"postalCode",
		];
		requiredFields.forEach((field) => {
			if (!formData[field]?.trim()) {
				newErrors[field] = "Required";
			}
		});
		if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "Invalid email";
		}
		const phoneDigits = formData.phoneNumber?.replace(/\D/g, "");
		if (formData.phoneNumber && phoneDigits.length !== 10) {
			newErrors.phoneNumber = "Invalid 10-digit phone";
		}
		if (
			formData.country === "Canada" &&
			formData.postalCode &&
			!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(formData.postalCode)
		) {
			newErrors.postalCode = "Invalid format (A1A 1A1)";
		} else if (
			formData.country === "USA" &&
			formData.postalCode &&
			!/^\d{5}(-\d{4})?$/.test(formData.postalCode)
		) {
			newErrors.postalCode = "Invalid format (12345 or 12345-6789)";
		}
		if (formData.province && formData.province.length !== 2) {
			newErrors.province = "Use 2-letter code";
		}
		setFormErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [formData]);

	// --- Input Change ---
	const handleInputChange = useCallback(
		(e) => {
			/* ... same logic ... */
			const { name, value } = e.target;
			setFormData((prev) => ({ ...prev, [name]: value }));
			if (formErrors[name]) {
				setFormErrors((prev) => ({ ...prev, [name]: undefined }));
			}
			if (name === "country") {
				setFormErrors((prev) => ({
					...prev,
					province: undefined,
					postalCode: undefined,
				}));
			}
		},
		[formErrors]
	);

	// --- Step 1 Submission (Confirm Address & Get Rates/Intent) ---
	const handleConfirmShipping = async (e) => {
		e.preventDefault();
		if (!validateForm()) {
			window.scrollTo({ top: 0, behavior: "smooth" });
			setToast({
				type: "error",
				message: "Please correct the errors in the form.",
			});
			return;
		}
		setLoadingStep1(true);
		setToast(null);
		setPaymentError(""); // Clear previous payment errors
		setClientSecret(""); // Clear previous client secret

		try {
			// --- Guest Handling ---
			if (!userInfo) {
				try {
					// Attempt login/signup silently - consider explicit guest checkout option?
					await dispatch(login(formData.email, null)); // How does guest login work?
					await addEmailToCartMailChimp(formData.email); // Add guest email
				} catch (error) {
					console.warn("Error processing guest email:", error);
					// Don't block checkout for this, proceed but maybe flag as guest
				}
			} else if (userInfo.email !== formData.email) {
				// If logged in user changed email? Unlikely but possible
				await addEmailToCartMailChimp(formData.email);
			}

			// --- Save Address ---
			await dispatch(saveShippingAddress(formData));

			// --- Get Shipping Rates ---
			const parcels = buildParcels(cartItems);
			const ship_to = buildShippingAddress(formData, countryState);
			const ratesData = await dispatch(
				getRates({ parcels, ship_to }, navigate)
			); // Handle potential navigation on error inside action?

			// Assuming ground is the default or only option for now
			const calculatedShipping =
				countryState === "USA"
					? parseFloat(ratesData.ground.total_charge.amount) || 0
					: 0; // Free for CA?
			setShippingRate(calculatedShipping);

			// --- Recalculate Totals with Shipping & Tax ---
			// Use the just set shippingRate state
			const updatedTotals = calculateTotals(
				cartItems,
				countryState,
				calculatedShipping,
				cartSummary?.withPromoPrice || 0,
				formData.province
			);
			setFinalTotals(updatedTotals); // Update state for display in Step 2
			console.log("Updated Totals:", updatedTotals);
			// --- Save Updated Cart Summary ---
			const cartSummaryUpdate = {
				...cartSummary, // Include previous promo info
				shippingAddress: formData, // Save the confirmed address
				shipping: calculatedShipping, // Save the calculated shipping
				FINAL_MSRP: updatedTotals.msrp,
				FINAL_PRICE: updatedTotals.ourPrice, // Price before promo
				FINAL_TAX: updatedTotals.tax, // Calculated tax
				SUBTOTAL: updatedTotals.ourPrice - (cartSummary?.withPromoPrice || 0), // Subtotal after discounts but before tax/shipping
				TOTAL: updatedTotals.subTotal, // Final amount including tax/shipping
				SAVINGS: updatedTotals.savings + (cartSummary?.withPromoPrice || 0), // Savings including promo
				email: formData.email,
				// Retain promoCode and withPromoPrice from original cartSummary
			};
			await dispatch(saveCartSummary(cartSummaryUpdate)); // Save to Redux state

			// --- Create Initial Order (isSubmitted: 0) ---
			// This is important if you need an order ID *before* payment (e.g., for Affirm)
			const orderData = {
				cartItems,
				cartSummary: cartSummaryUpdate,
				shippingAddress: formData,
				isSubmitted: 0,
				howDidYouHear: formData.howDidYouHear,
				email: formData.email,
				userInfo: userInfo,
			};
			// We need the insertId returned by createOrder action
			const createdOrderResult = await dispatch(createOrder(orderData));
			const initialOrderId = createdOrderResult?.insertId; // Check the structure returned by your action

			if (!initialOrderId) {
				throw new Error("Failed to initialize order. Please try again.");
			}
			console.log("Initial order created with ID:", initialOrderId);
			// Store initialOrderId if needed later? Maybe in Redux cart state?

			// --- Create Stripe Payment Intent ---
			// Only if Stripe is a potential payment method
			if (updatedTotals.subTotal > 0) {
				// Only create intent if there's an amount to pay
				try {
					const intentData = await createPaymentIntent({
						currency: countryState === "USA" ? "usd" : "cad",
						// Amount should be the *final total* in cents
						amount: Math.round(updatedTotals.subTotal * 100),
						items: cartItems.map((item) => ({
							// Simplified items for intent metadata
							name:
								item.productInfo?.OCS_Name || item.productInfo?.Type || "Shade",
							quantity: item.optionInfo?.quantity || 1,
							amount: Math.round(
								(parseFloat(item.optionInfo?.OUR_PRICE) || 0) * 100
							), // Item price in cents
						})),
						// Include Order ID in metadata for reconciliation
						metadata: {
							order_id: initialOrderId,
							customer_email: formData.email,
						},
					});
					if (!intentData?.clientSecret) {
						throw new Error("Failed to create Stripe payment intent.");
					}
					setClientSecret(intentData.clientSecret);
					// setDpmCheckerLink(intentData.dpmCheckerLink); // If using 3D Secure check link
				} catch (intentError) {
					console.error("Error creating payment intent:", intentError);
					// Allow proceeding but Stripe might fail? Or block?
					setToast({
						type: "warning",
						message:
							"Could not initialize Stripe payment. Please try another method or contact support.",
					});
				}
			} else {
				setClientSecret(""); // No payment needed
			}

			// --- Proceed to Step 2 ---
			setCurrentStep(2);
			window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll up
		} catch (error) {
			console.error("Error confirming shipping:", error);
			setToast({
				type: "error",
				message: `Error: ${
					error.message || "Could not calculate shipping or prepare payment."
				}`,
			});
		} finally {
			setLoadingStep1(false);
		}
	};

	// --- Step 2: Final Payment Success Handler ---
	const handlePaymentSuccess = useCallback(
		async (paymentResult) => {
			console.log("Payment Success Data:", paymentResult);
			if (!paymentResult || paymentResult.status !== "COMPLETED") {
				setPaymentError("Payment was not completed successfully.");
				setToast({
					type: "error",
					message: "Payment was not completed successfully.",
				});
				return;
			}

			setLoadingPayment(true); // Indicate final processing
			setPaymentError("");
			setToast(null);

			try {
				// 1. Save Payment Result
				await dispatch(savePaymentResult(paymentResult));

				// 2. Update Order Status (isSubmitted: 1) using initial order ID if stored, or refetch?
				// Assuming order ID is available in createdOrder from Redux state after initial creation
				const orderIdToUpdate = createdOrder?.insertId || cartSummary?.orderId; // Get order ID
				if (!orderIdToUpdate) {
					throw new Error(
						"Cannot finalize order: Initial Order ID is missing."
					);
				}

				await dispatch(
					createOrder({
						// Call createOrder again to UPDATE the status
						// Need to pass identifying info (order ID) and new status
						insertId: orderIdToUpdate, // Tell the action to update this order
						isSubmitted: 1,
						email: formData.email,
						// Optionally re-send other data if needed by backend update logic
						// cartItems, cartSummary: finalTotals, shippingAddress: formData, email: formData.email, ...
						paymentResult: paymentResult, // Send payment result for logging/association
					})
				);

				// 3. Post-Payment Actions (Mailchimp, Promo Code Invalidation, Email)
				if (userInfo?.email) addEmailToCartMailChimp(userInfo.email); // Or use formData.email
				if (cartSummary?.promoCode && cartSummary?.withPromoPrice > 0) {
					await dispatch(cancelPromoCode(userId || -1, cartSummary.promoCode)); // Invalidate used promo
				}
				// Send final confirmation email (maybe already done in handleOrder? If so, remove duplication)
				// If NOT done in handleOrder, do it here, potentially fetching fresh order details first.

				navigate("/confirmation", {
					state: { prevPath: "placeorder", orderId: orderIdToUpdate },
				}); // Redirect to confirmation
			} catch (error) {
				console.error("Error finalizing order after payment:", error);
				setPaymentError(
					`Error finalizing order: ${
						error.message || "Please contact support."
					}`
				);
				setToast({
					type: "error",
					message: `Error finalizing order. Please contact support.`,
				});
				setLoadingPayment(false); // Stop loading on error
			}
			// Don't set loading to false on success because we navigate away
		},
		[
			dispatch,
			navigate,
			cartItems,
			cartSummary,
			formData,
			finalTotals,
			userInfo,
			userId,
			createdOrder,
		]
	); // Include all dependencies

	// --- PayPal V2 Handlers ---
	const successPaymentHandlerV2 = (details, data) => {
		handlePaymentSuccess(details);
	}; // Adapt to useCallback version
	const errorPaymentHandlerV2 = (err) => {
		/* ... setPaymentError, setToast, setLoadingPayment(false) ... */
		console.error("PayPal Button v2 Error:", err);
		const errorMessage =
			typeof err === "string"
				? err
				: err?.message || "An unknown PayPal error occurred.";
		setPaymentError(`Payment failed: ${errorMessage}`);
		setToast({
			message: `Payment failed. Please check details.`,
			type: "error",
		});
		setLoadingPayment(false);
	};
	const cancelPaymentHandlerV2 = (data) => {
		/* ... console.log, setToast ... */
		console.log("PayPal Payment Cancelled:", data);
		setToast({ message: "Payment cancelled.", type: "info" });
	};

	return (
		<div className="min-h-screen bg-gray-100 py-8 md:py-12">
			<div className="container mx-auto px-4 max-w-6xl">
				{/* --- Header & Progress --- */}
				<div className="mb-8 md:mb-10">
					<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
						<h1 className="text-3xl md:text-4xl font-bold text-gray-900 flex items-center gap-2">
							<ShoppingCart className="w-8 h-8 text-primary hidden sm:inline-block" />{" "}
							Checkout
						</h1>
						{/* Simple Step Indicator */}
						<div className="flex items-center space-x-2 text-sm font-medium">
							<span
								className={`${
									currentStep === 1
										? "text-primary font-semibold"
										: "text-gray-500"
								}`}
							>
								1. Shipping
							</span>
							<span className="text-gray-300">→</span>
							<span
								className={`${
									currentStep === 2
										? "text-primary font-semibold"
										: "text-gray-400"
								}`}
							>
								2. Payment
							</span>
						</div>
					</div>
					{/* Back to Cart Link */}
					<Link
						to="/cart"
						className="text-xs text-gray-600 hover:text-primary inline-flex items-center gap-1"
					>
						<ChevronLeft size={14} /> Back to Cart
					</Link>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6 md:gap-8 items-start">
					{/* --- Left Column: Form / Payment --- */}
					<div className="space-y-6">
						{/* --- Step 1: Shipping Form --- */}
						<AnimatePresence mode="wait">
							{currentStep === 1 && (
								<motion.form
									key="step1-form"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									onSubmit={handleConfirmShipping}
									className="space-y-6"
								>
									{/* Contact Info Card */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
										<h2 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
											{" "}
											<User size={18} className="text-primary" /> Contact
											Information{" "}
										</h2>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<FormInput
												label="Email"
												type="email"
												name="email"
												value={formData.email}
												onChange={handleInputChange}
												error={formErrors.email}
												required
												icon={<Mail size={16} className="text-gray-400" />}
												disabled={loadingStep1}
											/>
											<FormInput
												label="Phone Number"
												type="tel"
												name="phoneNumber"
												value={formData.phoneNumber}
												onChange={handleInputChange}
												error={formErrors.phoneNumber}
												required
												icon={<Phone size={16} className="text-gray-400" />}
												placeholder="10 digits"
												disabled={loadingStep1}
											/>
										</div>
									</div>

									{/* Shipping Address Card */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
										<h2 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
											{" "}
											<MapPin size={18} className="text-primary" /> Shipping
											Address{" "}
										</h2>
										<div className="space-y-4">
											{/* ... FormInputs for name, address, city, etc. Add disabled={loadingStep1} ... */}
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
												{" "}
												<FormInput
													label="First Name"
													name="firstName"
													value={formData.firstName}
													onChange={handleInputChange}
													error={formErrors.firstName}
													required
													disabled={loadingStep1}
												/>{" "}
												<FormInput
													label="Last Name"
													name="lastName"
													value={formData.lastName}
													onChange={handleInputChange}
													error={formErrors.lastName}
													required
													disabled={loadingStep1}
												/>{" "}
											</div>
											<div>
												{" "}
												<label
													htmlFor="country"
													className="block text-sm font-medium mb-1 text-gray-700"
												>
													Country
												</label>{" "}
												<select
													id="country"
													name="country"
													value={formData.country}
													onChange={handleInputChange}
													className="form-select"
													disabled={loadingStep1}
												>
													{" "}
													<option value="Canada">Canada</option>{" "}
													<option value="USA">United States</option>{" "}
												</select>{" "}
											</div>
											<div>
												{" "}
												<FormInput
													label="Street Address"
													ref={placesRef}
													name="addressLine1"
													value={formData.addressLine1}
													onChange={handleInputChange}
													error={formErrors.addressLine1}
													required
													placeholder="Start typing..."
													disabled={loadingStep1}
												/>{" "}
											</div>
											<FormInput
												label="Apt, suite, etc. (Optional)"
												name="addressLine2"
												value={formData.addressLine2}
												onChange={handleInputChange}
												disabled={loadingStep1}
											/>
											<div className="grid grid-cols-1 sm:grid-cols-[2fr,1fr,1fr] gap-4">
												{" "}
												<FormInput
													label="City"
													name="city"
													value={formData.city}
													onChange={handleInputChange}
													error={formErrors.city}
													required
													disabled={loadingStep1}
												/>{" "}
												<FormInput
													label={
														formData.country === "USA" ? "State" : "Province"
													}
													name="province"
													maxLength={2}
													value={formData.province}
													onChange={handleInputChange}
													error={formErrors.province}
													required
													placeholder="2-letter code"
													disabled={loadingStep1}
												/>{" "}
												<FormInput
													label={
														formData.country === "USA"
															? "Zip Code"
															: "Postal Code"
													}
													name="postalCode"
													value={formData.postalCode}
													onChange={handleInputChange}
													error={formErrors.postalCode}
													required
													disabled={loadingStep1}
												/>{" "}
											</div>
										</div>
									</div>

									{/* How did you hear? Card */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
										<h2 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
											{" "}
											<Info size={18} className="text-primary" /> Additional
											Info{" "}
										</h2>
										<div>
											<label
												htmlFor="howDidYouHear"
												className="block text-sm font-medium mb-1 text-gray-700"
											>
												How did you hear about us?{" "}
												<span className="text-gray-500">(Optional)</span>
											</label>
											<select
												aria-label="How did you hear about us?"
												name="howDidYouHear"
												id="howDidYouHear"
												value={formData.howDidYouHear}
												onChange={handleInputChange}
												className={`form-select ${
													formErrors.howDidYouHear
														? "border-red-500"
														: "border-gray-300"
												}`}
												disabled={loadingStep1}
											>
												<option value="">Select an option</option>
												{/* ... options ... */}
												<option value="Facebook">Facebook</option>{" "}
												<option value="Instagram">Instagram</option>{" "}
												<option value="Social Media">Other Social Media</option>{" "}
												<option value="Search Engine">
													Google/Search Engine
												</option>{" "}
												<option value="Advertisement">Online Ad</option>{" "}
												<option value="Word of mouth">Friend/Referral</option>{" "}
												<option value="Other">Other</option>
											</select>
											{formErrors.howDidYouHear && (
												<p className="text-red-500 text-xs mt-1">
													{formErrors.howDidYouHear}
												</p>
											)}
										</div>
									</div>

									{/* Step 1 Submit Button */}
									<div className="pt-2">
										<button
											type="submit"
											disabled={loadingStep1 || cartItems.length === 0}
											className="w-full btn btn-primary py-3 text-base"
										>
											{loadingStep1 ? (
												<LoadingSpinner size="sm" text="Calculating..." />
											) : (
												<>
													Continue to Payment <ArrowRight size={18} />
												</>
											)}
										</button>
									</div>
								</motion.form>
							)}
						</AnimatePresence>

						{/* --- Step 2: Payment Section --- */}
						<AnimatePresence mode="wait">
							{currentStep === 2 && (
								<motion.div
									key="step2-payment"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									className="space-y-6"
								>
									{/* Address Review */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
										<div className="flex justify-between items-center mb-3">
											<h2 className="font-semibold text-lg flex items-center gap-2">
												<MapPin size={18} className="text-primary" /> Ship To
											</h2>
											<button
												onClick={() => setCurrentStep(1)}
												className="text-xs text-blue-600 hover:underline flex items-center gap-1"
												disabled={loadingPayment}
											>
												<Edit size={12} /> Change
											</button>
										</div>
										<div className="text-sm text-gray-700 space-y-0.5">
											<p>
												{formData.firstName} {formData.lastName}
											</p>
											<p>
												{formData.addressLine1}
												{formData.addressLine2 && `, ${formData.addressLine2}`}
											</p>
											<p>
												{formData.city}, {formData.province}{" "}
												{formData.postalCode}
											</p>
											<p>{formData.country}</p>
											<p>{formData.email}</p>
											<p>{formData.phoneNumber}</p>
										</div>
									</div>

									{/* Payment Method Selection */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
										<h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
											<CreditCard size={18} className="text-primary" /> Payment
											Method
										</h2>
										{paymentError && (
											<p className="text-red-600 text-sm mb-3 text-center bg-red-50 p-2 rounded border border-red-200">
												{paymentError}
											</p>
										)}
										<div className="grid grid-cols-3 gap-3">
											{/* Stripe */}
											<PaymentMethodCard
												selected={selectedPaymentMethod === "stripe"}
												onClick={() => setSelectedPaymentMethod("stripe")}
												icon={<CreditCard className="w-7 h-7 text-blue-600" />}
												name="Credit Card"
												value="stripe"
												disabled={loadingPayment || !clientSecret}
											/>
											{/* PayPal */}
											<PaymentMethodCard
												selected={selectedPaymentMethod === "paypal"}
												onClick={() => setSelectedPaymentMethod("paypal")}
												icon={
													<img
														src="/images/paypal.png"
														alt="PayPal"
														className="h-6"
													/>
												}
												name="PayPal"
												value="paypal"
												disabled={loadingPayment}
											/>
											{/* Affirm */}
											{formData.country === "Canada" && (
												<PaymentMethodCard
													selected={selectedPaymentMethod === "affirm"}
													onClick={() => setSelectedPaymentMethod("affirm")}
													icon={
														<img
															src="/images/affirm.png"
															alt="Affirm"
															className="h-5"
														/>
													}
													name="Affirm"
													value="affirm"
													disabled={loadingPayment}
												/>
											)}
										</div>
										{/* Divider */}
										<hr className="my-5 border-gray-100" />
										{/* Payment Element Area */}
										<div className="min-h-[150px]">
											{" "}
											{/* Min height while elements load */}
											{loadingPayment && (
												<div className="flex justify-center pt-10">
													<LoadingSpinner text="Processing Payment..." />
												</div>
											)}
											{!loadingPayment &&
												selectedPaymentMethod === "stripe" &&
												clientSecret && (
													<Elements
														stripe={stripePromise}
														options={{
															clientSecret,
															appearance: { theme: "stripe" },
														}}
													>
														<StripePaymentComponent
															successPaymentHandler={handlePaymentSuccess}
															payer={{
																name: `${formData.firstName} ${formData.lastName}`,
																email: formData.email,
															}}
															orderTotal={finalTotals.subTotal}
														/>
													</Elements>
												)}
											{!loadingPayment &&
												selectedPaymentMethod === "stripe" &&
												!clientSecret &&
												finalTotals.subTotal > 0 && (
													<p className="text-sm text-center text-orange-600 bg-orange-50 p-3 rounded border border-orange-200">
														Could not initialize card payment. Please try
														another method or refresh.
													</p>
												)}
											{!loadingPayment &&
												selectedPaymentMethod === "paypal" && (
													<PayPalButton
														amount={finalTotals.subTotal.toFixed(2)}
														currency={currencyCode} // Use calculated currencyCode
														onSuccess={successPaymentHandlerV2}
														onError={errorPaymentHandlerV2}
														onCancel={cancelPaymentHandlerV2}
														options={{
															clientId:
																process.env.NODE_ENV === "production"
																	? process.env.REACT_APP_PRODUCTION_PAYPAL_ID
																	: process.env.REACT_APP_SANDBOX_PAYPAL_ID,
															currency: currencyCode,
														}}
														style={{
															layout: "vertical",
															shape: "pill",
															height: 45,
														}}
													/>
												)}
											{!loadingPayment &&
												selectedPaymentMethod === "affirm" &&
												formData.country === "Canada" && (
													<AffirmCheckout
														onSuccess={
															handlePaymentSuccess
														} /* Pass necessary props */
													/>
												)}
											{/* Case for $0 order (e.g., 100% discount) */}
											{!loadingPayment && finalTotals.subTotal <= 0 && (
												<div className="text-center space-y-3">
													<p className="text-lg font-semibold text-green-600">
														Your order total is $0.00!
													</p>
													<button
														type="button"
														onClick={() =>
															handlePaymentSuccess({
																status: "COMPLETED",
																id: "FREE_ORDER",
																method: "Discount",
															})
														} // Simulate success for free order
														disabled={loadingPayment}
														className="btn btn-primary py-3"
													>
														{loadingPayment ? (
															<LoadingSpinner size="sm" />
														) : (
															"Complete Free Order"
														)}
													</button>
												</div>
											)}
										</div>
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</div>

					{/* --- Right Column: Summary (Sticky) --- */}
					<div className="lg:sticky lg:top-6 space-y-6">
						{/* Read-only Cart Summary */}
						<CartSummary
							totals={finalTotals} // Use the state holding the final calculated totals
							promoCode={cartSummary?.promoCode}
							promoAmount={cartSummary?.withPromoPrice || 0}
							// Disable promo interactions in checkout summary
							onPromoApply={null}
							onPromoChange={null}
							onPromoRemove={null}
							// Disable checkout button within summary
							onCheckout={null}
						/>
					</div>
				</div>

				{/* --- Toast Notifications --- */}
				<AnimatePresence>
					{" "}
					{toast && (
						<Toast
							show={!!toast}
							type={toast.type}
							message={toast.message}
							onClose={() => setToast(null)}
							duration={4000}
						/>
					)}{" "}
				</AnimatePresence>
			</div>
		</div>
	);
};

export default PlaceOrderScreen;
